import { Component, OnDestroy } from '@angular/core';
import { NavController, Platform, ToastController } from '@ionic/angular';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { ScanService } from '../../../providers/scan-service/scan-service';
import { ArianeeService } from '../../../providers/arianee-service/arianee.service';
import { PlatformLocation } from '@angular/common';
import { BehaviorSubject, from, Observable, Subscription } from 'rxjs';
import { ethers } from 'ethers';
import { Router } from '@angular/router';
import { HandleLinkService } from '../../../providers/scan-service/handle-link.service/handle-link.service';
import { EventLoggerService } from '../../../providers/event-logger/event-logger-service';
import { FeatureFlipService } from '../../../providers/feature-flip-service/feature-flip.service';
import { ToasterService } from '@arianeeprivate/wallet-shared-components';
import { DemoPassportService } from '../../../providers/demo-passport-service/demo-passport-service';
import { WalletConnectService } from '../../../providers/wallet-connect-service/wallet-connect.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-scan',
  templateUrl: './scan.page.html',
  styleUrls: ['./scan.page.scss']

})
export class ScanPage implements OnDestroy {
  public showDemoPassport = false;

  constructor (
    private scanService: ScanService,
    private toasterService:ToasterService,
    private navCtrl: NavController,
    private arianeeService: ArianeeService,
    private toastCtrl: ToastController,
    private location: PlatformLocation,
    private platform: Platform,
    private router:Router,
    private handleLinkService:HandleLinkService,
    private eventLogger: EventLoggerService,
    public featureFlipService:FeatureFlipService,
    private demoPassportService: DemoPassportService,
    private walletConnectService: WalletConnectService,
    private translateService:TranslateService
  ) { }

  public isIosNFCEnabled;
  public hasLight;
  public isLightOn = false;
  private subscriptions:Array<Subscription>=[];
  private sub;
  public scanType:string;
  public overlayImg:string;

  async ionViewDidEnter () {
    this.eventLogger.logScreen('scan', {});

    this.showDemoPassport = await this.featureFlipService.$isFeatureFlipOnce('demoPassport')
      .toPromise();

    this.activateQr();

    this.activateQrScan();
  }

  activateQr () {
    if (!this.sub) {
      this.sub = this.scanService.$scanEvents
        .pipe(
          mergeMap((link:string) => {
            if (ethers.utils.isValidMnemonic(<any>link)) {
              return this.handleMenmonic(<any>link);
            } else {
              return this.handleCertificateLink(link);
            }
          })
        )
        .subscribe(
          () => {},
          () => {
          }
        );
      this.subscriptions.push(this.sub);
    }

    if (this.platform.is('ios')) {
      this.isIosNFCEnabled = this.scanService.isNFCEnabled();
    }
    const sub = this.scanService.$scanActivated.subscribe(() => {
      this.hasLight = this.scanService.hasLight();
    });

    this.subscriptions.push(sub);
  }

  handleMenmonic (mnemonic):Observable<any> {
    const onOkHandler = () => {
      this.arianeeService.switchWallet(mnemonic)
        .pipe(
          tap(() => this.eventLogger.logEvent('scan_switchWallet_success')),
          take(1),
          mergeMap(() => from(this.router.navigate(['tab/brand-list'])))
        ).subscribe();
    };
    const cancelHandler = () => this.scanService.enableQrScanner(false);

    return this.toasterService.alert({
      backdropDismiss: false,
      message: 'Scan.importMnemonic.message',
      buttons: [
        {
          text: this.translateService.instant('Scan.importMnemonic.Ok'),
          cssClass: 'secondary',
          role: 'ok',
          handler: onOkHandler
        },
        {
          text: this.translateService.instant('Scan.importMnemonic.Cancel'),
          cssClass: 'secondary',
          role: 'cancel',
          handler: cancelHandler
        }
      ]
    });
  }

  handleCertificateLink (link):Observable<any> {
    return this.handleLinkService.handleLink(link)
      .pipe(
        catchError(err => {
          this.scanService.enableQrScanner(false);
          return new BehaviorSubject({ certificateId: -1 });
        }),
        filter(i => i.certificateId !== -1),
        map(async objectLink => {
          const network = await this.arianeeService.$walletInitialize.toPromise();
          await this.eventLogger.logEvent('scanned', {
            certificateId: objectLink.certificateId,
            method: objectLink.method
          });
          if (objectLink.certificateId) {
            this.redirectTo(`/tab/scan/product-detail/${network}/${objectLink.certificateId}/${objectLink.passphrase}/${objectLink.method}`);
          }
        }),
        tap(() => {
          this.scanService.disableQrScanner();
        })
      );
  }

  ngOnDestroy () {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.sub = undefined;
    this.isLightOn = false;
    this.scanService.disableQrScanner();
  }

  ionViewDidLeave () {
    this.ngOnDestroy();
  }

  redirectTo (url) {
    this.navCtrl.navigateForward(url);
  }

  toogleLight () {
    if (!this.isLightOn) {
      this.scanService.enableLight();
      this.isLightOn = true;
    } else {
      this.scanService.disableLight();
      this.isLightOn = false;
    }
  }

  async scanNFC () {
    await this.scanService.scanNFC();
    this.isIosNFCEnabled = this.scanService.isNFCEnabled();
  }

  public activateOCRScan () {
    if (this.scanType !== 'ocr') {
      this.scanType = 'ocr';
      this.scanService.enableOcrScanner();
      this.overlayImg = '/assets/imgs/overlay/watch.png';
    }
  }

  public activateQrScan () {
    this.scanType = 'qrcode';
    this.scanService.enableQrScanner();
    this.overlayImg = '/assets/imgs/overlay/qrcode.png';
  }

  public requestDemoPassport () {
    this.eventLogger.logEvent('getDemoPassport', {});
    this.demoPassportService.getDemoPassport();
  }
}
